import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { api, formatApiError } from "../lib/api";
import { formatoDinero } from "../lib/money";
import { FORMAS_PAGO, TEXTOS, texto, fechaCorta, rangoFechaSugerido, anticipoPorAplicar } from "../lib/anticipos";
import { toast } from "sonner";

// Diálogo al marcar una cita Cancelada o No asistió con un anticipo recibido sin aplicar
// (CL.4 A2). Decisión de Jesús (2 Oct): NADA se resuelve solo; la clínica decide — y puede
// decidir DESPUÉS («Decidir después» no llama a nada y el anticipo sigue por aplicar).
//
//   retener  → POST /api/anticipos/{id}/retener   (se queda como ingreso; crea un cobro)
//   devolver → POST /api/anticipos/{id}/devolver  {fecha?, forma_pago}
//
// Las dos exigen la capacidad `cobros_anular`: el padre sólo abre este diálogo si la tiene.
// El diálogo LEE el anticipo vivo de la cita en vez de fiarse de la Agenda: si otro usuario
// ya lo resolvió, se dice en vez de mandar una petición que va a dar 409.
export default function AnticipoDesenlaceDialog({ open, citaId, onPosponer, onHecho }) {
  const [anticipo, setAnticipo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [opcion, setOpcion] = useState("");
  const [forma, setForma] = useState("");
  const [fecha, setFecha] = useState("");
  const [fechaTocada, setFechaTocada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null); // {detail, code}
  const rango = rangoFechaSugerido();

  useEffect(() => {
    if (!open || !citaId) return;
    setAnticipo(null); setOpcion(""); setForma(""); setError(null);
    setFecha(rango.hoy); setFechaTocada(false);
    let vivo = true;
    setCargando(true);
    api
      .get("/anticipos", { params: { appointment_id: citaId } })
      .then(({ data }) => { if (vivo) setAnticipo(anticipoPorAplicar(data) || false); })
      .catch((e) => {
        if (vivo) setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, citaId]);

  if (!open) return null;

  const guardar = async () => {
    if (!anticipo) return;
    if (opcion !== "retener" && opcion !== "devolver") { setError({ detail: TEXTOS.desenlace_elige }); return; }
    if (opcion === "devolver" && !forma) { setError({ detail: TEXTOS.error_forma }); return; }
    setGuardando(true); setError(null);
    try {
      if (opcion === "retener") {
        await api.post(`/anticipos/${anticipo.id}/retener`, {});
        toast.success(TEXTOS.desenlace_retenido_ok);
      } else {
        const cuerpo = { forma_pago: forma };
        if (fechaTocada && fecha) cuerpo.fecha = fecha;
        await api.post(`/anticipos/${anticipo.id}/devolver`, cuerpo);
        toast.success(TEXTOS.desenlace_devuelto_ok);
      }
      onHecho?.();
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setGuardando(false);
    }
  };

  const CAMPO = "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-electric";

  return (
    <Modal
      open={open}
      onClose={guardando ? undefined : onPosponer}
      title={TEXTOS.desenlace_titulo}
      size="sm"
      testid="modal-anticipo-desenlace"
      footer={
        <>
          <button type="button" onClick={onPosponer} disabled={guardando}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                  data-testid="desenlace-posponer">
            {TEXTOS.desenlace_posponer}
          </button>
          {anticipo ? (
            <button type="button" onClick={guardar} disabled={guardando || !opcion}
                    className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60"
                    data-testid="desenlace-guardar">
              {guardando ? TEXTOS.desenlace_guardando : TEXTOS.desenlace_guardar}
            </button>
          ) : null}
        </>
      }
    >
      {cargando ? (
        <div className="py-6 text-center text-sm text-slate-500">{TEXTOS.cargando}</div>
      ) : anticipo === false ? (
        <p className="text-sm text-slate-600" data-testid="desenlace-sin-vivo">{TEXTOS.desenlace_sin_vivo}</p>
      ) : anticipo ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-700" data-testid="desenlace-intro">
            {texto("desenlace_intro", { monto: formatoDinero(anticipo.monto), fecha: fechaCorta(anticipo.fecha_recibido) })}
          </p>
          <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3 cursor-pointer">
            <input type="radio" name="desenlace" value="retener" checked={opcion === "retener"}
                   onChange={() => setOpcion("retener")} className="mt-1" data-testid="desenlace-retener" />
            <span>
              <span className="block text-sm font-medium text-slate-900">{TEXTOS.desenlace_retener}</span>
              <span className="block text-xs text-slate-500">{TEXTOS.desenlace_retener_ayuda}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3 cursor-pointer">
            <input type="radio" name="desenlace" value="devolver" checked={opcion === "devolver"}
                   onChange={() => setOpcion("devolver")} className="mt-1" data-testid="desenlace-devolver" />
            <span>
              <span className="block text-sm font-medium text-slate-900">{TEXTOS.desenlace_devolver}</span>
              <span className="block text-xs text-slate-500">{TEXTOS.desenlace_devolver_ayuda}</span>
            </span>
          </label>
          {opcion === "devolver" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500">{TEXTOS.desenlace_fecha}</label>
                <input type="date" className={CAMPO} value={fecha} max={rango.hoy} min={rango.min}
                       onChange={(e) => { setFecha(e.target.value); setFechaTocada(true); }}
                       data-testid="desenlace-fecha" />
              </div>
              <div>
                <label className="text-xs text-slate-500">{TEXTOS.desenlace_forma}</label>
                <select className={CAMPO} value={forma} onChange={(e) => setForma(e.target.value)}
                        data-testid="desenlace-forma">
                  <option value="">{TEXTOS.campo_forma_elige}</option>
                  {FORMAS_PAGO.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
             data-testid="desenlace-error">
          {error.detail}
        </div>
      )}
    </Modal>
  );
}
